import React from 'react';
import {
  CommonPageStep,
  ForgetPasswordPageStep,
  ResetPasswordPageStep,
} from '..';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  TextField,
} from '@mui/material';
import { bool } from 'prop-types';
import * as qs from 'query-string';
import { isMobileOnly } from 'react-device-detect';
import apiHelper, {
  SupportedIncomingFormat,
  SupportedMethod,
  SupportedOutgoingFormat,
  SupportedServers,
} from '../../common/ApiHelper';
import { verifyDataFormat } from '../../common/CommonTools';
import { appConfig } from '../../common/ConfigLoader';
import { useAppSelector } from '../../store/hooks';
import { setAppPageStep } from '../../store/reducer/appStateSlice';
import { outsideComponentDispatch } from '../../store/store';

export interface ResetPasswordElementInterface {
  passwordInputTextfield: JSX.Element;
  passwordRepeatInputTextfield: JSX.Element;
  submitButton: JSX.Element;
  loginButton: JSX.Element;
  forgetEmailButton: JSX.Element;
  resetPasswordSuccessfulIcon: JSX.Element;
}

export const ResetPasswordElement = (): ResetPasswordElementInterface => {
  // REDUX

  // HOOKS
  const [emailInputError, setEmailInputError] = React.useState(false);
  const [emailInput, setEmailInput] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [passwordInputError, setPasswordInputError] = React.useState(false);
  const [passwordRepeatInputError, setPasswordRepeatInputError] =
    React.useState(false);
  const [passwordValue, setPasswordValue] = React.useState('');
  const [repeatPasswordValue, setRepeatPasswordValue] = React.useState('');

  // ELEMENT FUNCTIONS
  const fetchQueryInfo = (): Record<string, unknown> => {
    // Get reset credentials from links
    const parmsValue = qs.parse(window.location.search);

    return { ...parmsValue };
  };

  const verifyPasswordDetail = (
    target: string,
  ): {
    lengthCheck: boolean;
    uppercaseCheck: boolean;
    lowercaseCheck: boolean;
    numberCheck: boolean;
  } => {
    const reLength = /^[\d\w.$@#!~*]{8,}$/; // Length and all content
    const reLowercase = /[a-z]/; // Lowercase character
    const reUppercase = /[A-Z]/; // Uppcase
    const reNumber = /\d/; // Numbers

    return {
      lengthCheck: reLength.test(target),
      uppercaseCheck: reUppercase.test(target),
      lowercaseCheck: reLowercase.test(target),
      numberCheck: reNumber.test(target),
    };
  };

  // ELEMENT
  const passwordInputTextfield: JSX.Element = (
    <React.Fragment>
      <FormControl
        sx={{ m: 0, width: 'stretch' }}
        variant="outlined"
        error={passwordInputError}
      >
        <InputLabel htmlFor="outlined-adornment-password">Password</InputLabel>
        <OutlinedInput
          id="outlined-adornment-password"
          type={showPassword ? 'text' : 'password'}
          sx={isMobileOnly ? { width: 'stretch' } : { width: '100%' }}
          value={passwordValue}
          fullWidth={true}
          onChange={(event): void => {
            setPasswordInputError(false);
            setPasswordValue(event.target.value);
            const verifyPassword = verifyDataFormat(event.target.value, {
              type: 'PASSWORD',
            });
            console.log(verifyPassword);
            setPasswordInputError(!verifyPassword);
          }}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={(): void => {
                  setShowPassword(!showPassword);
                }}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
          label="Password"
          aria-describedby="password-input-helper-text"
        />
        <FormHelperText id="password-input-helper-text">
          {passwordInputError ? (
            <React.Fragment>
              <Box style={{ display: 'flex', flexDirection: 'column' }}>
                {verifyPasswordDetail(passwordValue).lengthCheck ? (
                  <span style={{ color: '#4CAF50' }}>
                    ✅ Must be at least 8 characters
                  </span>
                ) : (
                  <span style={{ color: '#F44336' }}>
                    ❌ Must be at least 8 characters
                  </span>
                )}
                {verifyPasswordDetail(passwordValue).lowercaseCheck ? (
                  <span style={{ color: '#4CAF50' }}>
                    ✅ Must contain at least 1 lowercase
                  </span>
                ) : (
                  <span style={{ color: '#F44336' }}>
                    ❌ Must contain at least 1 lowercase
                  </span>
                )}
                {verifyPasswordDetail(passwordValue).uppercaseCheck ? (
                  <span style={{ color: '#4CAF50' }}>
                    ✅ Must contain at least 1 uppercase
                  </span>
                ) : (
                  <span style={{ color: '#F44336' }}>
                    ❌ Must contain at least 1 uppercase
                  </span>
                )}
                {verifyPasswordDetail(passwordValue).numberCheck ? (
                  <span style={{ color: '#4CAF50' }}>
                    ✅ Must contain at least 1 number
                  </span>
                ) : (
                  <span style={{ color: '#F44336' }}>
                    ❌ Must contain at least 1 number
                  </span>
                )}
              </Box>
            </React.Fragment>
          ) : (
            'Password must be at least 8 characters, contain lowercase, uppercase and numbers.'
          )}
        </FormHelperText>
      </FormControl>
    </React.Fragment>
  );

  const passwordRepeatInputTextfield: JSX.Element = (
    <React.Fragment>
      <FormControl
        sx={{ m: 0, width: 'stretch' }}
        variant="outlined"
        error={passwordRepeatInputError}
      >
        <InputLabel htmlFor="outlined-adornment-password">
          Repeat Password
        </InputLabel>
        <OutlinedInput
          id="outlined-adornment-password"
          type={showPassword ? 'text' : 'password'}
          sx={isMobileOnly ? { width: 'stretch' } : { width: '100%' }}
          value={repeatPasswordValue}
          fullWidth={true}
          onChange={(event): void => {
            setRepeatPasswordValue(event.target.value);
            setPasswordRepeatInputError(event.target.value !== passwordValue);
          }}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={(): void => {
                  setShowPassword(!showPassword);
                }}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
          label="Repeat Password"
          aria-describedby="password-repeat-input-helper-text"
        />
        <FormHelperText id="password-repeat-input-helper-text">
          {passwordRepeatInputError
            ? '❗️The password confirmation does not match.'
            : ''}
        </FormHelperText>
      </FormControl>
    </React.Fragment>
  );

  const submitButton = (
    <React.Fragment>
      <Button
        variant="contained"
        color="primary"
        style={{ marginTop: '32px' }}
        disabled={
          passwordInputError ||
          passwordRepeatInputError ||
          repeatPasswordValue === ''
        }
        onClick={async () => {
          if (
            passwordInputError ||
            passwordRepeatInputError ||
            repeatPasswordValue === ''
          ) {
            console.warn('INVALID PASSWORD INPUT');
            return;
          }

          const resetCredential = fetchQueryInfo();

          const resetPasswordEndpoint = 'user/UserResetPassword';
          const resetPasswordData = {
            ...resetCredential,
            password: passwordValue,
          };
          const resetPasswordResult = await apiHelper(
            SupportedServers.ACCOUNT,
            resetPasswordEndpoint,
            SupportedMethod.POST,
            resetPasswordData,
            {
              outgoingFormat: SupportedOutgoingFormat.JSON,
              incomingFormat: SupportedIncomingFormat.JSON,
            },
          );

          if (resetPasswordResult.data) {
            outsideComponentDispatch(
              setAppPageStep(ResetPasswordPageStep.CHANGED),
            );
          }
        }}
      >
        Submit
      </Button>
    </React.Fragment>
  );

  const loginButton = (
    <React.Fragment>
      <Button
        variant="contained"
        color="primary"
        style={{ marginTop: '32px' }}
        disabled={
          passwordInputError ||
          passwordRepeatInputError ||
          repeatPasswordValue === ''
        }
        onClick={async () => {
          outsideComponentDispatch(setAppPageStep(CommonPageStep.LOGIN));
        }}
      >
        Login
      </Button>
    </React.Fragment>
  );

  const forgetEmailButton = (
    <React.Fragment>
      <Button
        variant="text"
        color="primary"
        sx={{
          boxShadow: 'none',
        }}
      >
        forgot email?
      </Button>
    </React.Fragment>
  );

  const resetPasswordSuccessfulIcon = (
    <React.Fragment>
      <img
        id="login-main-back-button"
        alt=""
        src="assets/common/reset-password-successful-eddy.png"
        style={{ marginTop: '28px' }}
        onClick={() => {}}
      />
    </React.Fragment>
  );

  return {
    passwordInputTextfield,
    passwordRepeatInputTextfield,
    submitButton,
    loginButton,
    forgetEmailButton,
    resetPasswordSuccessfulIcon,
  };
};
